import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/api/trpc.ts';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import {
    canSelectNativeResearchSubscription,
    getStrategySubscriptionKey,
    type StrategySubscriptionActivationState,
    type StrategySubscriptionRow
} from './strategySubscription';

const NO_OPTIMISTIC_VALUES: ReadonlyMap<string, boolean> = new Map();
const NO_PENDING_KEYS: ReadonlySet<string> = new Set();

interface UseStrategySubscriptionActivationOptions {
    /**
     * Receives an activation request for a subscription whose deployment is incomplete. The switch is
     * disabled in that state, so this only fires when the row changed between render and click.
     */
    onDeploymentIncomplete: (row: StrategySubscriptionRow) => void;
}

/**
 * The portfolio subscription on/off switch, shared by the single-portfolio settings table and the
 * cross-portfolio subscriptions page so both apply the same guards, optimistic state, and live
 * confirmation.
 */
export const useStrategySubscriptionActivation = ({ onDeploymentIncomplete }: UseStrategySubscriptionActivationOptions) => {
    const queryClient = useQueryClient();

    /**
     * Activation values for toggles that are still in flight. Entries are dropped once the mutation
     * settles, so the row falls back to server state instead of holding a stale optimistic value when
     * the server rejects the change.
     */
    const [optimisticActiveByKey, setOptimisticActiveByKey] = useState<ReadonlyMap<string, boolean>>(NO_OPTIMISTIC_VALUES);
    const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(NO_PENDING_KEYS);
    /** A running strategy on a live portfolio waiting for the user to confirm it should be switched off. */
    const [pendingDeactivationRow, setPendingDeactivationRow] = useState<StrategySubscriptionRow | null>(null);

    const clearToggleState = useCallback((key: string) => {
        setOptimisticActiveByKey((current) => {
            if (!current.has(key)) return current;

            const remaining = new Map(current);
            remaining.delete(key);

            return remaining;
        });

        setPendingKeys((current) => {
            if (!current.has(key)) return current;

            const remaining = new Set(current);
            remaining.delete(key);

            return remaining;
        });
    }, []);

    const { mutate: saveStrategyActive } = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSettled: async (_data, _error, variables) => {
                if (variables.settingType !== 'strategyActive' || !variables.value || !('id' in variables.value)) {
                    throw new Error(`Expected a strategyActive payload, received ${variables.settingType}`);
                }

                // Refetch before dropping the optimistic value, so the switch never flashes back to its old position.
                await Promise.all([
                    queryClient.invalidateQueries(trpc.user.fetchPortfolio.pathFilter()),
                    queryClient.invalidateQueries(trpc.user.fetchPortfolios.pathFilter())
                ]);

                clearToggleState(getStrategySubscriptionKey(variables.portfolioId, variables.value.id));
            }
        }),
        {
            successMessage: 'Strategy updated successfully',
            fallbackErrorMessage: 'Failed to update strategy'
        }
    );

    const commitStrategyActive = useCallback(
        (row: StrategySubscriptionRow, active: boolean) => {
            setPendingKeys((current) => new Set(current).add(row.key));
            setOptimisticActiveByKey((current) => new Map(current).set(row.key, active));

            saveStrategyActive({
                portfolioId: row.portfolio.id,
                settingType: 'strategyActive',
                value: {
                    id: row.strategy.id,
                    active
                }
            });
        },
        [saveStrategyActive]
    );

    const requestToggle = useCallback(
        (row: StrategySubscriptionRow) => {
            if (row.isTogglePending) return;

            const shouldActivate = !row.isActive;

            // Defensive: the switch is disabled in this state, so this only fires if the row data changed
            // underneath the user between render and click.
            const researchSelection = canSelectNativeResearchSubscription(row.portfolio, row.strategy, row.readiness);
            if (shouldActivate && !row.readiness.isStrategyGloballyActive && !researchSelection) return;

            if (shouldActivate && !row.readiness.isDeploymentComplete && !researchSelection) {
                onDeploymentIncomplete(row);
                return;
            }

            // Switching off a strategy that trades real money takes effect immediately, so confirm it first.
            if (!shouldActivate && row.portfolio.type === 'live') {
                setPendingDeactivationRow(row);
                return;
            }

            commitStrategyActive(row, shouldActivate);
        },
        [commitStrategyActive, onDeploymentIncomplete]
    );

    const confirmDeactivation = useCallback(() => {
        if (!pendingDeactivationRow) throw new Error('No live strategy is waiting for deactivation confirmation');

        commitStrategyActive(pendingDeactivationRow, false);
        setPendingDeactivationRow(null);
    }, [commitStrategyActive, pendingDeactivationRow]);

    const cancelDeactivation = useCallback(() => setPendingDeactivationRow(null), []);

    const activationState = useMemo<StrategySubscriptionActivationState>(
        () => ({ optimisticActiveByKey, pendingKeys }),
        [optimisticActiveByKey, pendingKeys]
    );

    return { activationState, cancelDeactivation, confirmDeactivation, pendingDeactivationRow, requestToggle };
};
